/**
 * Pay-period arithmetic for internal time and the payroll hours export.
 *
 * A pay period is a fixed run of whole weeks counted from one anchor
 * Saturday (tenants.config.payroll.period_anchor_saturday), so every
 * period starts on a Saturday and ends on a Friday. Periods are numbered
 * from the anchor: period 0 starts on the anchor, period 1 the next
 * Saturday after it ends, period -1 the one ending the day before it.
 * "Today" is decided in the tenant's operating zone, so a Friday night in
 * New York that is already Saturday in UTC still belongs to the period
 * that is ending.
 *
 * An entry stays editable through a short grace after its period ends
 * (tenants.config.payroll.edit_grace_days), long enough to fix a missed
 * or mistyped line before the office runs payroll, and never on a date
 * that has not happened yet.
 *
 * api/_shared/payPeriod.ts is the server copy: identical logic, same fixture
 * table (tests/fixtures/payPeriod.json). Pure functions, no I/O.
 */

import { CAPACITY_TIME_ZONE, addDays, calendarDateIn } from './capacity';

export const DEFAULT_PERIOD_LENGTH_DAYS = 14;
export const DEFAULT_EDIT_GRACE_DAYS = 3;

export interface PayrollConfig {
  /** The anchor Saturday, YYYY-MM-DD. */
  anchor: string;
  /** Whole weeks, in days (7, 14, 28). */
  lengthDays: number;
  /** Days after a period ends during which its entries stay editable. */
  editGraceDays: number;
  /** The first day of the workweek (payroll.workweek_start_day; anything
   *  else reads as monday). Mirrors api/_shared/payPeriod.ts. */
  workweekStartDay: Weekday;
}

export const WORKWEEK_DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
export type Weekday = (typeof WORKWEEK_DAYS)[number];
export const DEFAULT_WORKWEEK_START_DAY: Weekday = 'monday';

export interface PayPeriod {
  /** Saturday, inclusive. */
  start: string;
  /** Friday, inclusive. */
  end: string;
}

export interface NumberedPayPeriod extends PayPeriod {
  /** Periods from the anchor: 0 starts on the anchor, negative before it. */
  index: number;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;
const SATURDAY = 6;

function utcDay(isoDate: string): number {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

/** True for a well-formed, real calendar date that falls on a Saturday. */
function isSaturdayIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !ISO_DATE_RE.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number) as [number, number, number];
  const probe = new Date(Date.UTC(y, m - 1, d));
  const real = probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
  return real && probe.getUTCDay() === SATURDAY;
}

/**
 * Read the payroll calendar from a tenants.config value
 * (config.payroll.period_anchor_saturday / period_length_days /
 * edit_grace_days). Null when there is no anchor or the anchor is not a
 * Saturday: a tenant with no payroll calendar has no pay periods, and no
 * caller should invent one. The length must be whole weeks and the grace
 * a whole number of days; anything else falls back to the platform default.
 */
export function readPayrollConfig(config: unknown): PayrollConfig | null {
  if (typeof config !== 'object' || config === null) return null;
  const payroll = (config as Record<string, unknown>).payroll;
  if (typeof payroll !== 'object' || payroll === null) return null;
  const p = payroll as Record<string, unknown>;
  if (!isSaturdayIsoDate(p.period_anchor_saturday)) return null;
  const length = p.period_length_days;
  const grace = p.edit_grace_days;
  return {
    anchor: p.period_anchor_saturday,
    lengthDays:
      typeof length === 'number' && Number.isInteger(length) && length > 0 && length % 7 === 0
        ? length
        : DEFAULT_PERIOD_LENGTH_DAYS,
    editGraceDays:
      typeof grace === 'number' && Number.isInteger(grace) && grace >= 0 ? grace : DEFAULT_EDIT_GRACE_DAYS,
    workweekStartDay: (WORKWEEK_DAYS as readonly unknown[]).includes(p.workweek_start_day)
      ? (p.workweek_start_day as Weekday)
      : DEFAULT_WORKWEEK_START_DAY,
  };
}

/** The nth period from the anchor (n may be negative). */
export function periodBounds(anchor: string, n: number, lengthDays: number = DEFAULT_PERIOD_LENGTH_DAYS): PayPeriod {
  const start = addDays(anchor, n * lengthDays);
  return { start, end: addDays(start, lengthDays - 1) };
}

/** The period containing `isoDate`, with its index from the anchor. */
export function periodFor(
  anchor: string,
  isoDate: string,
  lengthDays: number = DEFAULT_PERIOD_LENGTH_DAYS,
): NumberedPayPeriod {
  const days = Math.round((utcDay(isoDate) - utcDay(anchor)) / DAY_MS);
  const index = Math.floor(days / lengthDays);
  return { index, ...periodBounds(anchor, index, lengthDays) };
}

/** The period that is current at `now`, decided in the operating zone. */
export function currentPeriod(
  anchor: string,
  now: Date = new Date(),
  timeZone: string = CAPACITY_TIME_ZONE,
  lengthDays: number = DEFAULT_PERIOD_LENGTH_DAYS,
): NumberedPayPeriod {
  return periodFor(anchor, calendarDateIn(now, timeZone), lengthDays);
}

/**
 * May an entry dated `occurredOn` be written or changed on `today`? Yes
 * while the date has happened and today is no later than the last day of
 * its period plus the grace.
 */
export function isPeriodOpenForEdit(
  anchor: string,
  occurredOn: string,
  today: string,
  graceDays: number = DEFAULT_EDIT_GRACE_DAYS,
  lengthDays: number = DEFAULT_PERIOD_LENGTH_DAYS,
): boolean {
  if (occurredOn > today) return false;
  const { end } = periodFor(anchor, occurredOn, lengthDays);
  return today <= addDays(end, graceDays);
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function dayLabel(isoDate: string, withYear: boolean): string {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${WEEKDAYS[dow]} ${d} ${MONTHS[m - 1]}${withYear ? ` ${y}` : ''}`;
}

/** One day in the period label's short form, without the year: "Mon 2 Nov".
 *  The payroll screen's dates; the files keep YYYY-MM-DD. */
export function periodDayLabel(isoDate: string): string {
  return dayLabel(isoDate, false);
}

/**
 * "Sat 31 Oct – Fri 13 Nov 2026"; the start carries its own year only
 * when the period crosses into a new one ("Sat 26 Dec 2026 – Fri 8 Jan
 * 2027"). Fixed English names, so the label reads the same on every
 * machine.
 */
export function formatPeriodLabel(start: string, end: string): string {
  const sameYear = start.slice(0, 4) === end.slice(0, 4);
  return `${dayLabel(start, !sameYear)} – ${dayLabel(end, true)}`;
}
